import React from 'react';
import _ from 'lodash';

import {
  manualCreateLineItemEntryTypes,
  manualAdjustmentLineItemEntryTypes,
  carryoverAdjustmentLineItemEntryTypes
} from '../../Entities/LineItemEntry';

import InputDateField from '../Fields/InputDateField';
import InputSelectField from '../Fields/InputSelectField';
import InputCurrencyField from '../Fields/InputCurrencyField';

import {
  convertUnixMsToDateInTimezone,
  getCurrentDateInTimezone,
} from '../../Utils/date';

import withInvoice from '../Common/WithInvoice';
import featureFlags from '../../featureFlags';

import './InvoiceLineItemEntryTableRow.scss';

class InvoiceLineItemEntryTableRow extends React.Component {
  _onChange(inputId) {
    return value => {
      const { onChange } = this.props;

      const lineItemEntry = _.cloneDeep(this.props.lineItemEntry);
      lineItemEntry.fields[inputId].value = value;

      onChange(lineItemEntry);
    };
  }

  handleDeleteClick = () => {
    this.props.onChange(null);
  }

  getTypeOptions() {
    const {
      newMode,
      invoice,
      lineItemEntry,
      fieldConfiguration,
    } = this.props;

    const editMode = (lineItemEntry.fields.id.value === null);
    const lineItemTypeOptions = fieldConfiguration.lineItem.type.options;

    if (newMode) {
      return lineItemTypeOptions.filter(option => (
        manualCreateLineItemEntryTypes.includes(option.value)
      ));
    }

    if (editMode) {
      return lineItemTypeOptions.filter(option => (
        (invoice.isCarryover ? carryoverAdjustmentLineItemEntryTypes : manualAdjustmentLineItemEntryTypes).includes(option.value)
      ));
    }

    return lineItemTypeOptions;
  }

  getTransactionDate(lineItemEntry) {
    const {
      fieldConfiguration: {
        facilityTimezone
      }
    } = this.props;

    const transactionDate = lineItemEntry.fields.created_at.value;

    if (!transactionDate) return new Date(getCurrentDateInTimezone(facilityTimezone));

    return new Date(convertUnixMsToDateInTimezone(transactionDate, facilityTimezone));
  }
  
  getPaymentType(lineItemEntry) {
    const creditAppType = lineItemEntry.fields.line_item_payment?.value?.fields?.payment?.value?.fields?.credit_entry?.value?.credit?.application_type;
    if (creditAppType === 'DISCOUNT_CREDIT') {
      return 'Payment - Discount Credit Applied';
    } else if (creditAppType === 'CARRYOVER_CREDIT') {
      return 'Payment - Carryover Credit Applied';
    } else if (creditAppType === 'APPLY_CASH') {
      return 'Payment - Credit No Cash Applied';
    } else if (creditAppType === 'DO_NOT_APPLY_CASH') {
      return 'Payment - Credit Cash Applied';
    }
    return null;
  }

  /**
   * Get the expanded payment type label
   */
  getReversalType(lineItemEntry) {
    const creditReversalAppType = lineItemEntry.fields.line_item_reversal?.value?.fields?.payment?.value?.fields?.credit_entry?.value?.credit?.application_type;
    if (creditReversalAppType === 'APPLY_CASH') {
      return 'Reversal - Credit Payment';
    } else if (creditReversalAppType === 'DO_NOT_APPLY_CASH') {
      return 'Reversal - Credit Payment';
    }
    return null;
  }

  getUnapplyType(lineItemEntry) {
    const creditReversalAppType = lineItemEntry.fields.line_item_reversal?.value?.fields?.payment?.value?.fields?.credit_entry?.value?.credit?.application_type;
    if (creditReversalAppType === 'CARRYOVER_CREDIT') {
      return 'Unapply - Carryover Credit';
    } if (creditReversalAppType === 'DISCOUNT_CREDIT') {
      return 'Unapply - Discount Credit';
    }
    return null;
  }

  render() {
    const {
      lineItemEntry,
      inputId,
      newMode,
      taxExempt,
      adjustmentMode,
    } = this.props;

    const editMode = (lineItemEntry.fields.id.value === null);
    const entryIsCreditType = lineItemEntry.isCreditType();
    let grossAmountValue = lineItemEntry.getGrossAmount();

    const applicationDate  = lineItemEntry.fields.transaction.value?.fields.application_date.value;
    
    const paymentType = lineItemEntry.fields.type.value;

    let typeOptions = this.getTypeOptions();
    let paymentTypeLabel = this.getPaymentType(lineItemEntry);
    let reversalTypeLabel = this.getReversalType(lineItemEntry);
    let unapplyTypeLabel = this.getUnapplyType(lineItemEntry);
    
    if (paymentTypeLabel) {
      // Update the label for PAYMENT if this is an AC credit (paymentType non-null)
      typeOptions = typeOptions.map(({label, value}) => ({
        label: value === 'PAYMENT' ? paymentTypeLabel : label,
        value
      }));
    } else if (reversalTypeLabel) {
      // Update the label for REVERSAL if this is an AC credit reversal (reversalType non-null)
      typeOptions = typeOptions.map(({label, value}) => ({
        label: value === 'REVERSAL' ? reversalTypeLabel : label,
        value
      }));
    } else if (unapplyTypeLabel) {
      // Update the label for UNAPPLY_CREDIT if this is an AC credit reversal (unapplyType non-null)
      typeOptions = typeOptions.map(({label, value}) => ({
        label: value === 'UNAPPLY_CREDIT' ? unapplyTypeLabel : label,
        value
      }));
    }

    return (
      <div className="parkchirp--invoice-line-item-entry-table-row">
        <div className="parkchirp--invoice-line-item-entry-table--leading-spacer-column"></div>
        <div className="parkchirp--invoice-line-item-entry-table--date-column parkchirp--invoice-line-item-entry-table--cell">
          <InputDateField inputId={`${inputId}--created-at`}
            value={this.getTransactionDate(lineItemEntry)}
            editMode={false}
          />
        </div>
        {featureFlags.APPLICATION_DATE &&
          <div className="parkchirp--invoice-line-item-entry-table--date-column parkchirp--invoice-line-item-entry-table--cell">
            <InputDateField inputId={`${inputId}--application-date`}
              value={applicationDate ? new Date(applicationDate) : null}
              editMode={false}
            />
          </div>
        }
        <div className="parkchirp--invoice-line-item-entry-table--type-column parkchirp--invoice-line-item-entry-table--cell">
          <InputSelectField inputId={`${inputId}--type`}
            value={paymentType}
            onChange={this._onChange('type')}
            disabledMode={adjustmentMode ? false : true}
            options={(editMode) ? [ { value: '', label: '' }, ...typeOptions ] : typeOptions}
            editMode={editMode}
          />
        </div>
        <div className="parkchirp--invoice-line-item-entry-table--net-amount-column parkchirp--invoice-line-item-entry-table--cell">
          <InputCurrencyField inputId={`${inputId}--amount`}
            value={lineItemEntry.fields.amount.value}
            key={`amount--${lineItemEntry.fields.amount.value}`}
            onChange={this._onChange('amount')}
            editMode={editMode}
            negativeMode={entryIsCreditType}
          />
        </div>
        <div className="parkchirp--invoice-line-item-entry-table--amount-tax-1-column parkchirp--invoice-line-item-entry-table--cell">
          <InputCurrencyField inputId={`${inputId}--amount-tax-1`}
            value={lineItemEntry.fields.amount_tax_1.value}
            key={`amount-tax-1--${lineItemEntry.fields.amount_tax_1.value}`}
            onChange={this._onChange('amount_tax_1')}
            editMode={editMode}
            negativeMode={entryIsCreditType}
            disabledMode={taxExempt}
          />
        </div>
        <div className="parkchirp--invoice-line-item-entry-table--amount-tax-2-column parkchirp--invoice-line-item-entry-table--cell">
          <InputCurrencyField inputId={`${inputId}--amount-tax-2`}
            value={lineItemEntry.fields.amount_tax_2.value}
            key={`amount-tax-2--${lineItemEntry.fields.amount_tax_2.value}`}
            onChange={this._onChange('amount_tax_2')}
            editMode={editMode}
            negativeMode={entryIsCreditType}
            disabledMode={taxExempt}
          />
        </div>
        <div className="parkchirp--invoice-line-item-entry-table--amount-tax-3-column parkchirp--invoice-line-item-entry-table--cell">
          <InputCurrencyField inputId={`${inputId}--amount-tax-3`}
            value={lineItemEntry.fields.amount_tax_3.value}
            key={`amount-tax-3--${lineItemEntry.fields.amount_tax_3.value}`}
            onChange={this._onChange('amount_tax_3')}
            editMode={editMode}
            negativeMode={entryIsCreditType}
            disabledMode={taxExempt}
          />
        </div>
        <div className="parkchirp--invoice-line-item-entry-table--amount-tax-4-column parkchirp--invoice-line-item-entry-table--cell">
          <InputCurrencyField inputId={`${inputId}--amount-tax-4`}
            value={lineItemEntry.fields.amount_tax_4.value}
            key={`amount-tax-4--${lineItemEntry.fields.amount_tax_4.value}`}
            onChange={this._onChange('amount_tax_4')}
            editMode={editMode}
            negativeMode={entryIsCreditType}
            disabledMode={taxExempt}
          />
        </div>
        <div className="parkchirp--invoice-line-item-entry-table--gross-amount-column parkchirp--invoice-line-item-entry-table--cell">
          <InputCurrencyField inputId={`${inputId}--gross-amount`}
            value={grossAmountValue}
            key={`gross-amount--${grossAmountValue}`}
            editMode={false}
            negativeMode={entryIsCreditType}
          />
        </div>
        <div className="parkchirp--invoice-line-item-entry-table--actions-column parkchirp--invoice-line-item-entry-table--cell">
          {(!newMode && editMode) &&
            <button type="button"
              className="parkchirp--button-as-link"
              onClick={this.handleDeleteClick}>
              Delete
            </button>
          }
        </div>
      </div>
    );
  }
}

export default withInvoice(InvoiceLineItemEntryTableRow);
